<script setup lang="ts">
  import { ref, onMounted, computed, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { watchDebounced } from "@vueuse/core";
  import { useUsersStore } from "@stores/users";
  import { useAccountsStore } from "@stores/accounts";
  import { useRolesStore } from "@stores/roles";
  import { useCurrentUserStore } from "@stores/currentUser";
  import { roleKey, userType } from "@utils/constants/userRoles";
  import { Badge } from "@/components/ui/badge";
  import { Building2, KeyRound, Link2, Pencil, Trash2 } from "lucide-vue-next";
  import { useToastsStore } from "@stores/toastsStore";

  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { FilterDropdown } from "@/components/episto-ui/filter-dropdown";
  import { useUsersFilters } from "@/utils/composables/filters/useUsersFilters";
  import { DataTableActions, type DataTableColumn, type DataTableAction } from "@/components/episto-ui/data-table";
  import { IndexTable, IndexTableName, IndexTableEmpty } from "@/components/episto-ui/index-table";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
  import UserFormDialog from "./UserFormDialog.vue";
  import type { User, RoleGrantPayload } from "@/types";

  const usersStore = useUsersStore();
  const accountsStore = useAccountsStore();
  const rolesStore = useRolesStore();
  const currentUserStore = useCurrentUserStore();
  const toast = useToastsStore();
  const { users, usersCount, currentPage: storeCurrentPage } = storeToRefs(usersStore);
  const { roles } = storeToRefs(rolesStore);
  const { user: loggedInUser, isClient, isEpisto } = storeToRefs(currentUserStore);

  const PAGE_SIZE = 10;

  const USER_TYPE_LABELS: Record<string, string> = {
    [userType.SUPER_ADMIN]: "Super admin",
    [userType.ADMIN]: "Admin",
    [userType.SALES]: "Sales",
    [userType.CLIENT]: "Client",
  };

  const pageTitle = computed(() => (isClient.value ? "Members" : "Users"));
  const searchPlaceholder = computed(() => (isClient.value ? "Search members" : "Search users"));

  const isDialogOpen = ref(false);
  const isEditing = ref(false);
  const currentUser = ref<Partial<User>>({
    first_name: "",
    last_name: "",
    email: "",
    user_type: "client",
    user_roles: [],
  });
  const roleGrants = ref<RoleGrantPayload[]>([]);

  const searchQuery = ref("");

  const { filterConfigs, activeFiltersCount, handleFilterChange, handleClear, userTypeFilter, roleFilter, accountFilter } =
    useUsersFilters();

  const columns = computed<DataTableColumn<User>[]>(() => [
    { key: "user", label: "Name", width: 240 },
    { key: "email", label: "Email" },
    { key: "user_type", label: "Type", width: 110, visible: !isClient.value },
    { key: "roles", label: "Access", width: 320 },
  ]);

  const typeLabel = (user: User): string => USER_TYPE_LABELS[user.user_type ?? ""] ?? user.user_type ?? "";

  // The API resolves authorization per user (UserPolicy); the frontend only
  // decides which of the permitted actions to surface.
  const getActionsForUser = (user: User): DataTableAction[] => {
    if (!user.can_manage) return [];

    const actions: DataTableAction[] = [
      { key: "edit", label: "Edit", icon: Pencil },
      { key: "reset-password", label: "Reset Password", icon: KeyRound },
    ];

    if (user.can_delete) {
      actions.push({ key: "delete", label: "Delete", icon: Trash2, variant: "destructive", separatorBefore: true });
    }

    return actions;
  };

  type UserListParams = {
    page: number;
    search: string;
    userTypes: string[] | null;
    roles: number[] | null;
    account: number[] | null;
  };
  const fetchUsers = async (page = 1) => {
    await (usersStore.fetch as (params: UserListParams) => Promise<void>)({
      page,
      search: searchQuery.value,
      userTypes: userTypeFilter.value.length > 0 ? userTypeFilter.value : null,
      roles: roleFilter.value.length > 0 ? roleFilter.value : null,
      account: accountFilter.value.length > 0 ? accountFilter.value : null,
    });
  };

  onMounted(async () => {
    await Promise.all([fetchUsers(), accountsStore.fetch(), rolesStore.fetch()]);
  });

  watchDebounced(searchQuery, () => fetchUsers(1), { debounce: 300 });
  watch([userTypeFilter, roleFilter, accountFilter], () => fetchUsers(1), { deep: true });

  const roleName = (roleId: number): string => roles.value.find((role) => role.id === roleId)?.name || "Unknown";

  // Show the first grant only; the rest collapse into a +X badge to keep rows short
  const firstGrant = (user: User) => (user.user_roles ?? [])[0];
  const extraGrantsCount = (user: User) => Math.max((user.user_roles?.length ?? 0) - 1, 0);
  const isStaff = (user: User) => !!user.user_type && user.user_type !== userType.CLIENT;

  const handlePageChange = async (page: number) => {
    await fetchUsers(page);
  };

  const openCreateDialog = () => {
    isEditing.value = false;
    currentUser.value = {
      first_name: "",
      last_name: "",
      email: "",
      user_type: "client",
      user_roles: [],
    };
    const defaultAccountId = isClient.value ? loggedInUser.value?.default_account_id : null;
    const defaultRoleId = rolesStore.roleByKey(roleKey.CLIENT_EDIT)?.id;
    roleGrants.value =
      defaultAccountId && defaultRoleId
        ? [{ role_id: defaultRoleId, roleable_type: "Account", roleable_id: defaultAccountId }]
        : [];
    isDialogOpen.value = true;
  };

  const openEditDialog = (user: User) => {
    isEditing.value = true;
    currentUser.value = { ...user };
    roleGrants.value = (user.user_roles || []).map(({ role_id, roleable_type, roleable_id }) => ({
      role_id,
      roleable_type,
      roleable_id,
    }));
    isDialogOpen.value = true;
  };

  const errorMessage = (error: unknown): string => {
    if (Array.isArray(error)) return error.join(", ");
    if (typeof error === "string") return error;
    return "An error occurred";
  };

  const saveUser = async () => {
    try {
      const payload = {
        first_name: currentUser.value.first_name,
        last_name: currentUser.value.last_name,
        email: currentUser.value.email,
        user_type: currentUser.value.user_type,
        role_grants: currentUser.value.user_type ? roleGrants.value : [],
        crm_id: currentUser.value.crm_id,
        hs_job_title: currentUser.value.hs_job_title,
        hs_phone: currentUser.value.hs_phone,
      };

      if (isEditing.value && currentUser.value.id) {
        await usersStore.updateUser(currentUser.value.id, payload);
        toast.success("User updated successfully");
      } else {
        await usersStore.createUser(payload);
        toast.success("User created successfully");
      }
      isDialogOpen.value = false;
    } catch (error) {
      console.error(error);
      toast.error(errorMessage(error));
    }
  };

  const {
    isOpen: isDeleteAlertOpen,
    target: userToDelete,
    requestDelete: confirmDelete,
    confirm: handleDelete,
  } = useConfirmDelete<User>(async (user) => {
    try {
      await usersStore.deleteUser(user.id);
      toast.success("User deleted successfully");
    } catch {
      toast.error("Failed to delete user");
    }
  });

  const resetPassword = async (user: User) => {
    try {
      await usersStore.resetPassword(user.id);
      toast.success(`Password reset email sent to ${user.email}`);
    } catch {
      toast.error("Failed to send password reset email");
    }
  };

  const handleAction = (action: string, user: User): void => {
    switch (action) {
      case "edit":
        openEditDialog(user);
        break;
      case "delete":
        confirmDelete(user);
        break;
      case "reset-password":
        resetPassword(user);
        break;
    }
  };

  const userToDeleteName = computed(() => {
    if (!userToDelete.value) return null;
    return `${userToDelete.value.first_name} ${userToDelete.value.last_name}`;
  });
</script>

<template>
  <IndexPageLayout
    :title="pageTitle"
    :action-label="isClient ? 'New member' : 'New user'"
    @action="openCreateDialog">
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        :search-placeholder="searchPlaceholder">
        <template #filters>
          <FilterDropdown
            :filters="filterConfigs"
            :active-filters-count="activeFiltersCount"
            @toggle="handleFilterChange"
            @clear="handleClear" />
        </template>
      </IndexPageFilters>
    </template>

    <IndexTable
      compact
      :items="users"
      :columns="columns"
      :current-page="storeCurrentPage"
      :total="usersCount"
      :page-size="PAGE_SIZE"
      empty-message="No users found."
      @row-click="(item) => item.can_manage && openEditDialog(item)"
      @page-change="handlePageChange">
      <template #cell-user="{ item }">
        <IndexTableName :name="item.full_name || '—'" />
      </template>
      <template #cell-email="{ item }">
        <span
          class="block truncate text-muted-foreground"
          :title="item.email">
          {{ item.email }}
        </span>
      </template>
      <template #cell-user_type="{ item }">
        <Badge
          v-if="item.user_type"
          variant="neutral">
          {{ typeLabel(item) }}
        </Badge>
        <IndexTableEmpty v-else />
      </template>
      <template #cell-roles="{ item }">
        <span
          v-if="isStaff(item)"
          class="text-foreground-secondary">
          All accounts
        </span>
        <div
          v-else-if="firstGrant(item)"
          class="flex min-w-0 items-center gap-1.5">
          <Badge
            variant="outline"
            class="min-w-0 gap-1.5 font-normal text-foreground-secondary">
            <Building2
              v-if="firstGrant(item).roleable_type === 'Account'"
              class="text-foreground-subtle" />
            <Link2
              v-else
              class="text-foreground-subtle" />
            <span class="truncate">{{ firstGrant(item).roleable_name }}</span>
            <span class="shrink-0 text-border-strong">·</span>
            <span class="shrink-0 font-medium text-primary">{{ roleName(firstGrant(item).role_id) }}</span>
          </Badge>
          <Badge
            v-if="extraGrantsCount(item)"
            variant="outline"
            class="shrink-0 font-normal text-foreground-subtle">
            +{{ extraGrantsCount(item) }}
          </Badge>
        </div>
        <span
          v-else
          class="text-foreground-subtle">
          No access
        </span>
      </template>
      <template #actions="{ item }">
        <div
          class="flex items-center justify-end"
          @click.stop>
          <DataTableActions
            v-if="getActionsForUser(item).length > 0"
            :actions="getActionsForUser(item)"
            @action="(action) => handleAction(action, item)" />
          <span
            v-else
            class="size-7" />
        </div>
      </template>
    </IndexTable>

    <template #dialogs>
      <UserFormDialog
        v-model:open="isDialogOpen"
        v-model:user="currentUser"
        v-model:role-grants="roleGrants"
        :is-editing="isEditing"
        :roles="roles"
        :can-edit-user-type="isEpisto"
        @save="saveUser" />

      <ConfirmDialog
        v-model:open="isDeleteAlertOpen"
        title="Are you absolutely sure?"
        confirm-label="Delete"
        @confirm="handleDelete">
        This action cannot be undone. This will permanently delete the user
        <span class="font-bold">{{ userToDeleteName }}</span>
        and remove all associated data from our servers.
      </ConfirmDialog>
    </template>
  </IndexPageLayout>
</template>
